import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, ChevronLeft, ChevronRight, PlayCircle, FileText, ArrowLeft, Download } from "lucide-react";
import api, { apiErr, API } from "@/lib/api";

const ytId = (url) => {
  const m = (url || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : "";
};
const vimeoId = (url) => {
  const m = (url || "").match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : "";
};

function VideoPlayer({ lesson }) {
  if (!lesson) return null;
  const type = lesson.video_type || (lesson.video_url ? "youtube" : "");
  if (type === "file" && lesson.video_file) {
    const src = lesson.video_file.startsWith("http") ? lesson.video_file : `${API.replace(/\/api$/, "")}${lesson.video_file}`;
    return (
      <video key={src} controls controlsList="nodownload" className="w-full h-full" data-testid="classroom-video-file">
        <source src={src} />
      </video>
    );
  }
  if (type === "vimeo" || vimeoId(lesson.video_url)) {
    const id = vimeoId(lesson.video_url);
    if (id) return <iframe title={lesson.title} src={`https://player.vimeo.com/video/${id}`} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen className="w-full h-full" data-testid="classroom-video-vimeo" />;
  }
  const yt = ytId(lesson.video_url);
  if (yt) return <iframe title={lesson.title} src={`https://www.youtube.com/embed/${yt}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-testid="classroom-video-youtube" />;
  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 gap-2">
      <PlayCircle className="h-10 w-10" />
      <p className="text-sm">No video for this lesson yet.</p>
    </div>
  );
}

export default function Classroom() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState(null); // lesson id
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    api.get(`/courses/${slug}/classroom`)
      .then((r) => {
        setData(r.data);
        setCompleted(r.data.completed_lessons || []);
        setProgress(r.data.progress || 0);
        const first = (r.data.course.curriculum || []).flatMap((m) => m.lessons)[0];
        setActive(first?.id || null);
      })
      .catch((e) => {
        if (e?.response?.status === 401) { navigate("/login"); return; }
        setDenied(true);
        toast.error(apiErr(e));
      });
  }, [slug, navigate]);

  const flat = useMemo(() => (data?.course?.curriculum || []).flatMap((m, mi) => m.lessons.map((l) => ({ ...l, module: m.title, mi }))), [data]);
  const activeLesson = flat.find((l) => l.id === active);
  const activeIdx = flat.findIndex((l) => l.id === active);

  const toggleComplete = async (lessonId, value) => {
    try {
      const { data: res } = await api.post("/my/lesson-complete", { course_slug: slug, lesson_id: lessonId, completed: value });
      setCompleted(res.completed_lessons);
      setProgress(res.progress);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (denied) return (
    <div className="py-32 text-center text-slate-500" data-testid="classroom-denied">
      <p>You need to enrol in this course to access the classroom.</p>
      <Link to={`/courses/${slug}`} className="text-navy underline mt-3 inline-block">View course</Link>
    </div>
  );
  if (!data) return <div className="py-32 text-center text-slate-400">Loading classroom…</div>;

  const course = data.course;
  const isDone = (id) => completed.includes(id);

  return (
    <div className="bg-subtle min-h-screen" data-testid="classroom-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link to={`/courses/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy transition-colors mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to course
        </Link>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Player + notes */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-black rounded-xl overflow-hidden aspect-video shadow-lg" data-testid="classroom-player">
              <VideoPlayer lesson={activeLesson} />
            </div>
            <div className="bg-white border border-border rounded-xl p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs text-gold font-semibold uppercase tracking-wide">{activeLesson?.module}</p>
                  <h1 className="font-display text-2xl font-bold text-navy mt-1" data-testid="classroom-lesson-title">{activeLesson?.title}</h1>
                </div>
                {activeLesson && (
                  <button
                    onClick={() => toggleComplete(activeLesson.id, !isDone(activeLesson.id))}
                    data-testid="mark-complete-btn"
                    className={`shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-semibold transition-colors ${isDone(activeLesson.id) ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-navy text-white hover:bg-navy-light"}`}
                  >
                    {isDone(activeLesson.id) ? <><CheckCircle2 className="h-4 w-4" /> Completed</> : <>Mark complete</>}
                  </button>
                )}
              </div>
              {activeLesson?.content && (
                <div className="mt-5 text-slate-600 leading-relaxed whitespace-pre-line text-sm" data-testid="classroom-notes">{activeLesson.content}</div>
              )}
              {activeLesson?.resources?.length > 0 && (
                <div className="mt-5 space-y-2">
                  {activeLesson.resources.map((r, i) => (
                    <a key={i} href={r.url?.startsWith("http") ? r.url : `${API.replace(/\/api$/, "")}${r.url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-navy hover:text-gold transition-colors">
                      <Download className="h-4 w-4" /> {r.label || r.filename || "Resource"}
                    </a>
                  ))}
                </div>
              )}
              <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
                <button disabled={activeIdx <= 0} onClick={() => setActive(flat[activeIdx - 1].id)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 disabled:opacity-30 hover:text-navy transition-colors" data-testid="prev-lesson-btn">
                  <ChevronLeft className="h-4 w-4" /> Previous
                </button>
                <button disabled={activeIdx >= flat.length - 1} onClick={() => setActive(flat[activeIdx + 1].id)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 disabled:opacity-30 hover:text-navy transition-colors" data-testid="next-lesson-btn">
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 bg-white border border-border rounded-xl overflow-hidden">
              <div className="p-5 border-b border-border">
                <p className="font-display text-lg font-bold text-navy">{course.title}</p>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} data-testid="classroom-progress-bar" />
                  </div>
                  <span className="text-xs font-semibold text-slate-500">{progress}%</span>
                </div>
              </div>
              <div className="max-h-[65vh] overflow-y-auto divide-y divide-border">
                {(course.curriculum || []).map((m, mi) => (
                  <div key={mi} className="py-2">
                    <p className="px-5 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">Module {mi + 1}: {m.title}</p>
                    {m.lessons.map((l) => (
                      <button
                        key={l.id}
                        onClick={() => setActive(l.id)}
                        data-testid={`lesson-item-${l.id}`}
                        className={`w-full text-left px-5 py-2.5 flex items-center gap-3 text-sm transition-colors ${active === l.id ? "bg-gold/10 text-navy font-medium" : "text-slate-600 hover:bg-subtle"}`}
                      >
                        {isDone(l.id) ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <Circle className="h-4 w-4 text-slate-300 shrink-0" />}
                        <span className="flex-1">{l.title}</span>
                        {l.duration && <span className="text-xs text-slate-400">{l.duration}</span>}
                      </button>
                    ))}
                  </div>
                ))}
                {flat.length === 0 && <p className="px-5 py-8 text-sm text-slate-400 text-center">No lessons added to this course yet.</p>}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
